import axios from 'axios';

// API 기본 URL 설정
const API_URL = process.env.REACT_APP_API_URL || '/api';

// 인증 필요 시 처리 함수 (로그인 페이지로 리다이렉트)
const handleAuthRequired = () => {
  console.warn('[DashboardService] JWT 인증 실패 감지. 로그인 페이지로 이동합니다.');
  
  // 현재 URL을 세션 스토리지에 저장하여 로그인 후 돌아올 수 있도록 함
  if (window.location.pathname !== '/login' && window.location.pathname !== '/select-tenant') {
    sessionStorage.setItem('redirect_after_login', window.location.pathname);
  }
  
  // 쿠키 정리 (dashboard_id는 백엔드에서 관리)
  document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'current_tenant=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  
  // 로그인 페이지로 즉시 리다이렉트
  window.location.href = '/login';
  
  return null;
};

// API 요청 옵션 기본값
const defaultOptions = {
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // HttpOnly 쿠키를 위해 credentials 포함
};

const DashboardService = {
  /**
   * 대시보드 템플릿 조회
   * @param {string} dashboardId 대시보드 ID
   * @param {boolean} strict 엄격 모드 (폴백 사용 안함)
   * @returns {Promise<Object>} 대시보드 템플릿 데이터
   */
  getDashboardTemplate: async (dashboardId, strict = false) => {
    try {
      // strict 모드에서는 반드시 특정 ID가 필요하며 폴백 사용 안함
      if (strict && (!dashboardId || dashboardId === 'undefined' || dashboardId === 'system_default')) {
        throw new Error(`Invalid dashboard ID for strict mode: ${dashboardId}`);
      }
      
      const dashboardIdToUse = dashboardId && dashboardId !== 'undefined' ? dashboardId : 'system_default';
      // strict 모드에서는 no_fallback 파라미터 추가
      const url = strict 
        ? `${API_URL}/visualization/dashboards/${dashboardIdToUse}/?no_fallback=true`
        : `${API_URL}/visualization/dashboards/${dashboardIdToUse}/`;
      
      const response = await axios.get(url, defaultOptions);
      
      // 백엔드 응답 데이터의 컴포넌트 위치 확인
      const components = response.data?.components || response.data?.template?.components;
      if (components) {
        components.forEach(comp => {
          if (comp.id && comp.id.includes('pie')) {
          }
        });
      }
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 대시보드 템플릿 조회 오류:', {
        dashboardId,
        strict,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      // 404 에러 명시적 처리
      if (error.response?.status === 404) {
        return {
          success: false,
          error: `대시보드를 찾을 수 없습니다 (ID: ${dashboardId})`,
          status: 404
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || '대시보드 템플릿 조회 실패',
        status: error.response?.status
      };
    }
  },

  /**
   * 사용자 대시보드 목록 조회
   * @returns {Promise<Object>} 사용자의 대시보드 목록
   */
  getUserDashboards: async () => {
    try {
      const response = await axios.get(
        `${API_URL}/visualization/user-dashboards/`,
        defaultOptions
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('사용자 대시보드 목록 조회 오류:', error);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || '대시보드 목록 조회 실패'
      };
    }
  },

  /**
   * 대시보드 컴포넌트 데이터 조회
   * @param {Object} params 쿼리 파라미터
   * @returns {Promise<Object>} 컴포넌트 데이터
   */
  getComponentData: async (params) => {
    try {
      // data_source 형태로 전달받은 경우 UpdateChartData API 형식으로 변환
      const requestBody = params.data_source ? {
        sql: params.data_source.query,
        chart_template: {
          chart_options: {
            viz_type: 'table'
          }
        },
        variables: params.variables || {}
      } : params;
      
      const response = await axios.post(
        `${API_URL}/visualization/UpdateChartData/`,
        requestBody,
        defaultOptions
      );
      
      // UpdateChartData 응답 구조: { chart_data: { chart_options: { data: { rows, columns } } } }
      const rows = response.data.chart_data?.chart_options?.data?.rows;
      const columns = response.data.chart_data?.chart_options?.data?.columns;
      
      // rows를 객체 배열로 변환
      let dataArray = [];
      if (rows && Array.isArray(rows) && columns && Array.isArray(columns)) {
        dataArray = rows.map(row => {
          const obj = {};
          columns.forEach((col, idx) => {
            obj[col] = row[idx];
          });
          return obj;
        });
      }
      
      return {
        success: true,
        data: {
          data: dataArray
        }
      };
    } catch (error) {
      console.error('컴포넌트 데이터 조회 오류:', error);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || '컴포넌트 데이터 조회 실패'
      };
    }
  },

  /**
   * 차트 데이터 업데이트
   * @param {Object} params 업데이트 파라미터
   * @returns {Promise<Object>} 업데이트 결과
   */
  updateChartData: async (params) => {
    try {
      const response = await axios.post(
        `${API_URL}/visualization/UpdateChartData/`,
        params,
        defaultOptions
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('차트 데이터 업데이트 오류:', error);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || '차트 데이터 업데이트 실패'
      };
    }
  },

  /**
   * 대시보드 저장
   * @param {Object} dashboard 저장할 대시보드 데이터
   * @param {string} currentDashboardId 현재 편집 중인 대시보드 ID
   * @returns {Promise<Object>} 저장 결과
   */
  saveDashboard: async (dashboard, currentDashboardId = null) => {
    try {
      // 백엔드가 기대하는 형식으로 데이터를 감쌈
      const requestData = {
        new_dashboard_template: dashboard, // 실제 대시보드 객체
        explanation: "Dashboard updated from frontend", // 간단한 설명 추가
        current_dashboard_id: currentDashboardId // 현재 편집 중인 대시보드 ID 추가
      };
      
      console.log('[DashboardService.saveDashboard] 저장 요청:', {
        hasGroups: !!dashboard.groups,
        groupsCount: dashboard.groups?.length,
        componentsWithGroupId: dashboard.components?.filter(c => c.group_id).length,
        url: `${API_URL}/visualization/update_dashboard_template/`
      });

      const response = await axios.post(
        `${API_URL}/visualization/update_dashboard_template/`,
        requestData, // 수정된 데이터 객체 사용
        defaultOptions
      );
      
      console.log('[DashboardService.saveDashboard] 저장 응답:', response.data);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 대시보드 저장 오류:', error);
      console.error('[DashboardService] 오류 응답:', error.response?.data);
      console.error('[DashboardService] 오류 상태:', error.response?.status);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      // 409 Conflict 오류일 경우 충돌 정보를 반환
      if (error.response?.status === 409) {
        return {
          success: false,
          isConflict: true,
          conflictInfo: error.response.data.conflict_info || null,
          error: error.response.data.conflict_info?.message || '대시보드 ID 충돌이 발생했습니다.'
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || error.message || '대시보드 저장 실패'
      };
    }
  },

  /**
   * 대시보드 저장 (옵션 포함)
   * @param {Object} dashboard 저장할 대시보드 데이터
   * @param {Object} options 저장 옵션 (force_replace, generate_new_id, current_dashboard_id 등)
   * @returns {Promise<Object>} 저장 결과
   */
  saveDashboardWithOptions: async (dashboard, options = {}) => {
    try {
      // 백엔드가 기대하는 형식으로 데이터를 감쌈
      const requestData = {
        new_dashboard_template: dashboard, // 실제 대시보드 객체
        explanation: "Dashboard updated from frontend with options", // 간단한 설명 추가
        ...options // force_replace, generate_new_id, current_dashboard_id 등의 옵션 추가
      };

      const response = await axios.post(
        `${API_URL}/visualization/update_dashboard_template/`,
        requestData,
        defaultOptions
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 대시보드 저장 오류 (옵션 포함):', error);
      console.error('[DashboardService] 오류 응답:', error.response?.data);
      console.error('[DashboardService] 오류 상태:', error.response?.status);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      // 409 Conflict 오류일 경우 충돌 정보를 반환
      if (error.response?.status === 409) {
        return {
          success: false,
          isConflict: true,
          conflictInfo: error.response.data.conflict_info || null,
          error: error.response.data.conflict_info?.message || '대시보드 ID 충돌이 발생했습니다.'
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || error.message || '대시보드 저장 실패'
      };
    }
  },

  /**
   * 대시보드 ID 중복 체크
   * @param {string} dashboardId 체크할 대시보드 ID
   * @param {string} currentDashboardId 현재 편집 중인 대시보드 ID (선택사항)
   * @returns {Promise<Object>} 중복 체크 결과
   */
  validateDashboardId: async (dashboardId, currentDashboardId = null) => {
    try {
      const requestData = {
        dashboard_id: dashboardId,
        current_dashboard_id: currentDashboardId
      };

      const response = await axios.post(
        `${API_URL}/visualization/validate_dashboard_id/`,
        requestData,
        defaultOptions
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 대시보드 ID 중복 체크 오류:', error);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || '대시보드 ID 중복 체크 실패'
      };
    }
  },

  /**
   * LLM을 사용한 대시보드 컴포넌트 생성
   * @param {string} naturalLanguageRequest 자연어 요청
   * @param {number} updateInterval 업데이트 간격 (초)
   * @param {Object} existingComponentTemplate 기존 컴포넌트 템플릿 (선택사항)
   * @returns {Promise<Object>} 생성된 컴포넌트
   */
  createDashboardComponent: async (naturalLanguageRequest, updateInterval = 60, existingComponentTemplate = null) => {
    try {
      const requestData = {
        natural_language_request: naturalLanguageRequest,
        update_interval: updateInterval
      };
      
      // 기존 템플릿이 있으면 추가
      if (existingComponentTemplate) {
        requestData.existing_component_template = existingComponentTemplate;
      }
      
      const response = await axios.post(
        `${API_URL}/dashboard/component/`,
        requestData,
        defaultOptions
      );
      
      // 차트 컴포넌트 생성/수정 후 토큰 사용량 업데이트
      // TODO: api.js 파일이 필요한 경우 구현 필요
      /*
      try {
        // api 모듈 동적 가져오기
        const api = (await import('../services/api')).default;
        if (api && typeof api.notifyTokenUpdate === 'function') {
          api.notifyTokenUpdate();
        }
      } catch (tokenError) {
        console.error('차트 컴포넌트 생성/수정 후 토큰 업데이트 오류:', tokenError);
      }
      */
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 컴포넌트 생성 오류:', error);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || '컴포넌트 생성 실패'
      };
    }
  },

  /**
   * 대시보드 삭제
   * @param {string} dashboardId 삭제할 대시보드 ID
   * @returns {Promise<Object>} 삭제 결과
   */
  deleteDashboard: async (dashboardId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/visualization/delete_dashboard_template/${dashboardId}/`,
        defaultOptions
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[DashboardService] 대시보드 삭제 오류:', error);
      console.error('[DashboardService] 오류 응답:', error.response?.data);
      console.error('[DashboardService] 오류 상태:', error.response?.status);
      
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      
      // 404 Not Found 오류일 경우
      if (error.response?.status === 404) {
        return {
          success: false,
          error: '삭제하려는 대시보드를 찾을 수 없습니다.'
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || error.message || '대시보드 삭제 실패'
      };
    }
  },

  /**
   * 허용된 DB 함수 목록 조회
   * @returns {Promise<Object>} 함수 목록 응답
   */
  getAllowedDBFunctions: async () => {
    try {
      const response = await axios.get(
        `${API_URL}/dashboard/function/allowed/`,
        defaultOptions
      );

      return response.data;
    } catch (error) {
      console.error('[DashboardService] DB 함수 목록 조회 오류:', error);

      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }

      return {
        success: false,
        error: error.response?.data?.error || error.message || 'DB 함수 목록 조회 실패'
      };
    }
  },

  /**
   * DB 함수 소스 코드 조회 (슈퍼관리자 전용)
   * @param {Object} params 파라미터
   * @param {string} params.schema 스키마명 (예: 'public')
   * @param {string} [params.name] 함수명 (예: 'update_status')
   * @param {string} [params.identity_args] 정체성 인자 문자열 (예: 'integer, text')
   * @param {string} [params.signature] 함수 시그니처 (예: 'update_status(integer, text)')
   * @returns {Promise<Object>} { success, definition, error }
   */
  getFunctionSource: async (params) => {
    try {
      const response = await axios.post(
        `${API_URL}/dashboard/function/source/`,
        params,
        defaultOptions
      );
      return response.data;
    } catch (error) {
      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }
      // 403 Forbidden - 권한 없음
      if (error.response?.status === 403) {
        return {
          success: false,
          error: error.response?.data?.error || '함수 소스에 접근할 권한이 없습니다'
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || error.message || '함수 소스 조회 실패'
      };
    }
  },

  /**
   * DB 함수 실행
   * @param {Object} params 실행 파라미터
   * @param {string} params.functionName 함수명
   * @param {Object} params.parameters 함수 파라미터
   * @param {string} params.schema 스키마명 (선택)
   * @returns {Promise<Object>} 실행 결과
   */
  executeDBFunction: async (params) => {
    try {
      const response = await axios.post(
        `${API_URL}/dashboard/function/execute/`,
        params,
        defaultOptions
      );

      return response.data;
    } catch (error) {
      console.error('[DashboardService] DB 함수 실행 오류:', error);

      // 401 Unauthorized 오류일 경우 로그인 화면으로 이동
      if (error.response?.status === 401) {
        return handleAuthRequired();
      }

      // 403 Forbidden - 권한 없음
      if (error.response?.status === 403) {
        return {
          success: false,
          error: error.response?.data?.error || '이 함수를 실행할 권한이 없습니다'
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || error.message || 'DB 함수 실행 실패'
      };
    }
  }
};

export default DashboardService; 